import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, Download, Eye, FileSpreadsheet, FileText, Loader2, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import MediaCaptureField from '../common/MediaCaptureField.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import SettlementFilterBar from '../common/SettlementFilterBar.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import CompanySettlementBatchView from '../common/CompanySettlementBatchView.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { formInputClass, formSelectClass } from '../common/uiClasses.js';
import { downloadBlob } from '../../utils/downloadBlob.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIstDateTime, getCurrentMonthRange } from '../../utils/localDate.js';
import { useAuth } from '../../context/AuthContext.js';
import companySettlementService from '../../services/companySettlementService.js';
import { locationHubService } from '../../services/locationHubService.js';
import HubMultiSelect from '../dashboard/components/HubMultiSelect.js';
import SelectField from '../common/SelectField.js';
import ListPagination from '../common/ListPagination.js';
import { settlementReviewerName } from '../common/SettlementAuditFields.js';
import { PAYMENT_MODES, statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const TABS = [
  { id: 'pending', label: 'Pending', statusParam: 'OCR_PENDING,HUMAN_REVIEW,PENDING' },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
  { id: 'company_paid', label: 'Company paid', statusParam: '' },
  { id: 'company_owes', label: 'Company owes', statusParam: '' },
];

const OWES_PAGE_SIZE = 10;

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const hubIdsFromSearch = (searchParams) => {
  const raw = searchParams.get('hub_ids') || searchParams.get('location_hub_ids') || '';
  return raw.split(',').map((id) => id.trim()).filter(Boolean);
};

const dashboardBackTo = (role) => {
  if (role === 'Accountant') return '/accountant-dashboard';
  if (role === 'System Admin') return '/system-admin-dashboard';
  return '/admin-dashboard';
};

const canReview = (status) => (
  status === 'HUMAN_REVIEW' || status === 'PENDING' || status === 'RECORDING'
);

const isLiveSavingRow = (row) => {
  if (row?._pending) return companySettlementService.isSettlementJobOpen();
  return false;
};

const excelCell = (value, type = 'String') => {
  const text = String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<Cell><Data ss:Type="${type}">${text}</Data></Cell>`;
};

const downloadLinesExcel = (filename, lines) => {
  const rows = Array.isArray(lines) ? lines : [];
  const total = rows.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  const body = rows.map((line) => (
    '<Row>'
    + excelCell(line.ml_sheet_id || '')
    + excelCell(line.location || '')
    + excelCell(Number(line.amount || 0).toFixed(2), 'Number')
    + '</Row>'
  )).join('');
  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Records"><Table>
<Row>${excelCell('ML Sheet')}${excelCell('Source')}${excelCell('Amount')}</Row>
${body}
<Row>${excelCell('Total')}${excelCell('')}${excelCell(total.toFixed(2), 'Number')}</Row>
</Table></Worksheet></Workbook>`;
  downloadBlob(xml, filename, 'application/vnd.ms-excel');
};

const excelName = (label) => {
  const safe = String(label || 'records').replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'records';
  return `${safe}.xls`;
};

const canDeleteSettlement = (role, status) => (
  (role === 'System Admin' || role === 'Admin') && status === 'APPROVED'
);

const canEditSettlement = canDeleteSettlement;

const reviewFields = (row) => {
  const nested = (row.paid_requests || []).concat(row.to_pay_requests || [])[0] || {};
  return {
    ...nested,
    ...row,
    status: row.status,
    reviewed_at: row.reviewed_at || nested.reviewed_at,
    reviewed_by_display: row.reviewed_by_display || nested.reviewed_by_display,
    reviewed_by_name: row.reviewed_by_name || nested.reviewed_by_name,
  };
};

const AccountantCompanySettlementApprovals = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const month = getCurrentMonthRange();
  const [tab, setTab] = useState(() => {
    const asked = searchParams.get('tab');
    return TABS.some((item) => item.id === asked) ? asked : 'pending';
  });
  const [fromDate, setFromDate] = useState(() => searchParams.get('from_date') || month.fromDate);
  const [toDate, setToDate] = useState(() => searchParams.get('to_date') || month.toDate);
  const [hubIds, setHubIds] = useState(() => hubIdsFromSearch(searchParams));
  const [agentId, setAgentId] = useState(() => (searchParams.get('agent_user_id') || '').trim());
  const [agentOptions, setAgentOptions] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, company_paid: 0, company_owes: 0 });
  const [owesPage, setOwesPage] = useState(1);
  const [review, setReview] = useState(null);
  const [companyPayments, setCompanyPayments] = useState([]);
  const [owesAgents, setOwesAgents] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [owesView, setOwesView] = useState(null);
  const [paidView, setPaidView] = useState(null);
  const [payAgentId, setPayAgentId] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [payRemarks, setPayRemarks] = useState('');
  const [payFile, setPayFile] = useState(null);
  const [paying, setPaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [owesLoading, setOwesLoading] = useState(false);
  const [owesFetched, setOwesFetched] = useState(false);
  const [owesTotal, setOwesTotal] = useState(0);
  const loadSeq = useRef(0);
  const summarySeq = useRef(0);
  const owesSeq = useRef(0);
  const owesKeyRef = useRef('');
  const settledAgentIds = useRef(new Set());
  const owesSectionRef = useRef(null);
  const scrollOwesRef = useRef(false);
  const [busyId, setBusyId] = useState('');
  const [viewBatch, setViewBatch] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  const statusParam = TABS.find((t) => t.id === tab)?.statusParam || '';

  const filterParams = useCallback(() => ({
    ...dateParams(fromDate, toDate),
    ...(hubIds.length ? { location_hub_ids: hubIds } : {}),
    ...(agentId ? { agent_user_id: agentId } : {}),
  }), [fromDate, toDate, hubIds, agentId]);

  const loadBatches = useCallback(async (options = {}) => {
    const quiet = options.quiet === true;
    const requestId = ++loadSeq.current;
    const params = filterParams();
    if (!quiet) setLoading(true);
    try {
      const listRes = await companySettlementService.listBatches({
        status: statusParam,
        ...params,
      });
      if (requestId !== loadSeq.current) return;
      const payments = listRes?.company_payments || [];
      const incoming = listRes?.data || [];
      const remittedAmount = incoming
        .filter((row) => String(row.status || '').toUpperCase() === 'APPROVED')
        .reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
      const pendingRows = incoming.filter((row) => {
        const status = String(row.status || '').toUpperCase();
        return !['APPROVED', 'REJECTED', 'RECORDING'].includes(status);
      });
      setReview((prev) => ({
        ...(prev || {}),
        approved_paid_amount: remittedAmount,
        pending_review_count: pendingRows.length,
        pending_review_amount: pendingRows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0),
      }));
      const pending = tab === 'approved' ? companySettlementService.pendingRecordedSettlement() : null;
      const serverHasSaving = incoming.some((row) => (
        row.status === 'RECORDING'
        && pending
        && String(row.agent_user_id) === String(pending.agent_user_id)
      ));
      const showPending = Boolean(pending)
        && companySettlementService.isSettlementJobOpen()
        && !serverHasSaving;
      if (pending && !showPending) companySettlementService.clearPendingSettlement(false);
      setRows(showPending ? [pending, ...incoming] : incoming);
      setCompanyPayments(payments);
      setAgentOptions(listRes?.filter_options?.agents || []);
      const serverCounts = listRes?.counts || { pending: 0, approved: 0, rejected: 0 };
      setCounts((prev) => ({
        ...prev,
        ...serverCounts,
        approved: Number(serverCounts.approved || 0) + (showPending ? 1 : 0),
        company_paid: payments.length,
      }));
    } catch (err) {
      if (requestId !== loadSeq.current) return;
      Alert.error(err.response?.data?.error || 'Failed to load settlements');
    } finally {
      if (requestId === loadSeq.current) setLoading(false);
    }
  }, [statusParam, filterParams, tab]);

  const loadSummary = useCallback(async (options = {}) => {
    const quiet = options.quiet === true;
    const requestId = ++summarySeq.current;
    const params = filterParams();
    if (!quiet) setSummaryLoading(true);
    try {
      const sumRes = await companySettlementService.getSummary({ ...params, cards: 1 });
      if (requestId !== summarySeq.current) return;
      const summary = sumRes?.data || {};
      const cards = summary.review || {};
      setReview((prev) => ({
        ...(prev || {}),
        due_to_company: cards.due_to_company,
        paid_due: cards.paid_due,
        to_pay_due: cards.to_pay_due,
        approved_paid_amount: prev?.approved_paid_amount,
        pending_review_count: prev?.pending_review_count,
        pending_review_amount: prev?.pending_review_amount,
      }));
    } catch (err) {
      if (requestId !== summarySeq.current) return;
      if (err?.code === 'ERR_CANCELED') return;
    } finally {
      if (requestId === summarySeq.current) setSummaryLoading(false);
    }
  }, [filterParams]);

  const loadCompanyOwes = useCallback(async (force = false) => {
    const params = filterParams();
    const key = JSON.stringify(params);
    if (!force && owesKeyRef.current === key && owesSeq.current > 0) {
      return;
    }
    const requestId = ++owesSeq.current;
    owesKeyRef.current = key;
    setOwesLoading(true);
    try {
      const res = await companySettlementService.listCompanyOwes(params);
      if (requestId !== owesSeq.current) return;
      const payload = res?.data;
      const serverOwed = Array.isArray(payload) ? payload : (payload?.items || []);
      const stillOwed = new Set(serverOwed.map((row) => String(row.user_id)));
      settledAgentIds.current.forEach((id) => {
        if (!stillOwed.has(id)) settledAgentIds.current.delete(id);
      });
      const owed = serverOwed.filter((row) => !settledAgentIds.current.has(String(row.user_id)));
      const total = res?.total != null
        ? Number(res.total)
        : owed.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      setOwesAgents(owed);
      setOwesTotal(total);
      setOwesFetched(true);
      setCounts((prev) => ({ ...prev, company_owes: Number(res?.count != null ? res.count : owed.length) }));
    } catch (err) {
      if (requestId !== owesSeq.current) return;
      owesKeyRef.current = '';
      Alert.error(err.response?.data?.error || 'Failed to load company owes');
    } finally {
      if (requestId === owesSeq.current) setOwesLoading(false);
    }
  }, [filterParams]);

  const loadData = useCallback(async () => {
    await loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (!cancelled) loadSummary();
    }, 1200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [loadSummary]);

  const openPayToAgents = () => {
    scrollOwesRef.current = true;
    setTab('company_owes');
    loadCompanyOwes();
  };

  const onStatusTabChange = (next) => {
    setTab(next);
    if (next === 'company_owes' || next === 'company_paid') {
      loadCompanyOwes();
    }
  };

  useEffect(() => {
    if (tab !== 'company_owes' || !scrollOwesRef.current) return undefined;
    scrollOwesRef.current = false;
    const frame = window.requestAnimationFrame(() => {
      owesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'approved') return undefined;
    if (!rows.some((row) => row.status === 'RECORDING')) return undefined;
    const timer = window.setInterval(() => loadBatches({ quiet: true }), 3000);
    return () => window.clearInterval(timer);
  }, [tab, rows, loadBatches]);

  useEffect(() => {
    const refresh = () => loadBatches({ quiet: true });
    const pending = companySettlementService.currentSettlementJob();
    if (pending && typeof pending.finally === 'function') pending.finally(refresh);
    if (companySettlementService.settlementJobSettledRecently()) refresh();
    window.addEventListener('company-settlement-changed', refresh);
    return () => window.removeEventListener('company-settlement-changed', refresh);
  }, [loadBatches]);

  useEffect(() => {
    setOwesPage(1);
    owesKeyRef.current = '';
  }, [fromDate, toDate, hubIds, agentId]);

  useEffect(() => {
    locationHubService.getAllHubs(1, 500, { all: true, brief: 1 })
      .then((res) => {
        const data = res.data || {};
        setHubs(data.hubs || data.items || data.location_hubs || data.data || []);
      })
      .catch(() => setHubs([]));
  }, []);

  const onApprove = (batchId) => {
    if (busyId === batchId) return;
    setBusyId(batchId);
    setViewBatch(null);
    const job = companySettlementService.approveBatch(batchId)
      .then((res) => {
        Alert.success(res?.message || 'Approved');
      })
      .catch((err) => {
        Alert.error(err.response?.data?.error || 'Approve failed');
      })
      .finally(() => {
        setBusyId('');
        loadBatches({ quiet: true });
      });
    companySettlementService.trackSettlementJob(job);
  };

  const onEdit = (row) => {
    setEditBatch(row);
    setEditAmount(String(row.total_amount ?? ''));
    setOpenMenuId(null);
  };

  const saveEdit = () => {
    if (!editBatch || busyId === editBatch.batch_id) return;
    const amount = Number(editAmount);
    if (!amount || amount <= 0) {
      Alert.error('Enter an amount above 0. Delete the settlement to remove it.');
      return;
    }
    const batchId = editBatch.batch_id;
    setBusyId(batchId);
    companySettlementService.editBatch(batchId, amount)
      .then((res) => {
        Alert.success(res?.message || 'Settlement amount updated');
        setEditBatch(null);
        loadBatches({ quiet: true });
      })
      .catch((err) => {
        Alert.error(err.response?.data?.error || 'Could not update this amount');
      })
      .finally(() => setBusyId(''));
  };

  const onDelete = async (batchId) => {
    const confirmed = await Alert.confirm(
      'Delete this settlement? The agent amount will go back up by this settlement.',
      'Delete Settlement',
    );
    if (!confirmed || busyId === batchId) return;
    setOpenMenuId(null);
    setBusyId(batchId);
    setViewBatch(null);
    // Drop any list request that started before this delete so it cannot put the row back.
    loadSeq.current += 1;
    try {
      const res = await companySettlementService.deleteBatch(batchId);
      setRows((current) => current.filter((row) => row.batch_id !== batchId));
      setCounts((prev) => ({
        ...prev,
        approved: Math.max(0, Number(prev.approved || 0) - 1),
      }));
      Alert.success(res?.message || 'Settlement deleted');
      await loadBatches({ quiet: true });
      loadSummary();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Delete failed');
      loadBatches({ quiet: true });
    } finally {
      setBusyId('');
    }
  };

  const onReject = async (batchId) => {
    const notes = window.prompt('Rejection notes (optional)');
    if (notes === null) return;
    try {
      setBusyId(batchId);
      const res = await companySettlementService.rejectBatch(batchId, { review_notes: notes });
      Alert.success(res?.message || 'Rejected');
      setViewBatch(null);
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Reject failed');
    } finally {
      setBusyId('');
    }
  };

  const paySelected = owesAgents.find((row) => String(row.user_id) === String(payAgentId)) || owesAgents[0];

  const onPayAgent = async () => {
    const agent = owesAgents.find((row) => String(row.user_id) === String(payAgentId)) || owesAgents[0];
    if (!agent) {
      Alert.error('Select the agent to pay');
      return;
    }
    setPaying(true);
    setPayOpen(false);
    const form = new FormData();
    form.append('agent_user_id', agent.user_id);
    form.append('from_date', fromDate || '');
    form.append('to_date', toDate || '');
    if (hubIds.length) form.append('location_hub_ids', hubIds.join(','));
    form.append('payment_mode', payMode);
    form.append('remarks', payRemarks || '');
    if (payFile) form.append('file_attachment', payFile);
    const job = companySettlementService.payAgent(form)
      .then((res) => {
        const paid = res?.data || {};
        settledAgentIds.current.add(String(agent.user_id));
        setOwesAgents((current) => current.filter((row) => String(row.user_id) !== String(agent.user_id)));
        setCounts((prev) => ({
          ...prev,
          company_owes: Math.max(0, Number(prev.company_owes || 0) - 1),
        }));
        if (paid.expense_id) {
          setCompanyPayments((current) => [{
            expense_id: paid.expense_id,
            amount: paid.amount,
            paid_to: paid.paid_to || agent.name,
            paid_user_id: agent.user_id,
            payment_mode: payMode,
            payment_date: new Date().toISOString(),
            lines: paid.lines || agent.sources || [],
          }, ...current.filter((row) => row.expense_id !== paid.expense_id)]);
        }
        Alert.success(res?.message || 'Payment recorded');
        setPayRemarks('');
        setPayFile(null);
      })
      .catch((err) => {
        Alert.error(err.response?.data?.error || 'Payment failed');
        setPayOpen(true);
      })
      .finally(() => {
        setPaying(false);
        loadBatches({ quiet: true });
        loadCompanyOwes(true);
        loadSummary({ quiet: true });
      });
    companySettlementService.trackSettlementJob(job);
  };

  const exportParams = {
    status: statusParam,
    ...filterParams(),
  };

  const downloadExcel = async () => {
    try {
      await companySettlementService.downloadExcel(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel download failed');
    }
  };

  const downloadPdf = async () => {
    try {
      await companySettlementService.downloadPdf(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF download failed');
    }
  };

  const openView = async (row) => {
    try {
      const res = await companySettlementService.getBatch(row.batch_id);
      setViewBatch(res?.data || row);
    } catch (err) {
      setViewBatch(row);
    }
  };

  const owesTotalPages = Math.max(1, Math.ceil(owesAgents.length / OWES_PAGE_SIZE));
  const owesPageSafe = Math.min(owesPage, owesTotalPages);
  const owesSlice = owesAgents.slice(0, owesPageSafe * OWES_PAGE_SIZE);
  const canSettleOwes = user?.role !== 'Hub Admin';

  const payToAgents = owesFetched
    ? owesTotal
    : owesAgents.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  const showMoney = (value) => (summaryLoading ? '…' : fullCurrency(value));
  const dueToCompany = Number(review?.due_to_company || 0);
  const paidDue = Number(review?.paid_due || 0);
  const toPayDue = Number(review?.to_pay_due || 0);
  const remitted = Number(review?.approved_paid_amount || 0);
  const pendingCount = Number(review?.pending_review_count || 0);
  const pendingAmount = Number(review?.pending_review_amount || 0);

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Settlement Approvals"
        subtitle={
          user?.role === 'Hub Admin'
            ? 'Open dues for this hub. Remit them on Settle with Company. This page is for requests waiting on accountant approve.'
            : 'One approve settles Paid remittance and To-Pay remittance. Cashbook posts after approve.'
        }
        icon={Check}
        backTo={dashboardBackTo(user?.role)}
      >
        <Button variant="secondary" icon={FileSpreadsheet} mobileIconOnly onClick={downloadExcel}>
          Excel
        </Button>
        <Button variant="secondary" icon={FileText} mobileIconOnly onClick={downloadPdf}>
          PDF
        </Button>
      </ListPageHeader>

      <SettlementFilterBar
        statusLabel="Status"
        tabs={TABS}
        activeTab={tab}
        onTabChange={onStatusTabChange}
        counts={counts}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        centerStatus
        singleRow
        extraLeft={(
          <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
            <div className="w-full min-w-0 sm:w-44">
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Hub / Branch</label>
              <HubMultiSelect
                hubs={hubs}
                value={hubIds}
                onChange={setHubIds}
                placeholder="All Hubs"
                compact
                className="!w-full !min-w-0 !max-w-none"
                buttonClassName="!min-w-0 !max-w-none"
              />
            </div>
            <div className="w-full min-w-0 sm:w-48">
              <SelectField
                variant="filter"
                portal
                label="Agent"
                labelClassName="block text-xs font-medium text-gray-500 mb-1.5"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="All agents"
                emptyLabel="All agents"
                options={[
                  { value: '', label: 'All agents' },
                  ...agentOptions.map((agent) => {
                    const hub = String(agent.location_hub_name || '').trim();
                    const name = agent.name || agent.user_id;
                    return {
                      value: String(agent.user_id),
                      label: hub ? `${name} (${hub})` : name,
                    };
                  }),
                ]}
              />
            </div>
          </div>
        )}
      />

      <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-5">
          <div className="min-w-0 bg-white rounded-xl border border-amber-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Due to company</p>
            <p className="mt-1 text-lg font-semibold text-amber-800 tabular-nums">{showMoney(dueToCompany)}</p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-sky-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Paid / To-Pay due</p>
            <p className="mt-1 text-lg font-semibold text-sky-800 tabular-nums">{showMoney(paidDue + toPayDue)}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Paid {showMoney(paidDue)} · To-Pay {showMoney(toPayDue)}
            </p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-emerald-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Remitted (approved)</p>
            <p className="mt-1 text-lg font-semibold text-emerald-800 tabular-nums">{showMoney(remitted)}</p>
          </div>
          <div className="min-w-0 bg-white rounded-xl border border-orange-100 p-3 sm:p-4">
            <p className="text-xs text-gray-500">Pending review</p>
            <p className="mt-1 text-lg font-semibold text-orange-800 tabular-nums">{showMoney(pendingAmount)}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {summaryLoading ? '…' : pendingCount} request{pendingCount === 1 ? '' : 's'}
            </p>
          </div>
          <button
            type="button"
            onClick={openPayToAgents}
            className="min-w-0 bg-white rounded-xl border border-emerald-300 p-3 text-left transition-colors hover:bg-emerald-50/50 sm:p-4"
          >
            <p className="text-xs text-gray-500">Pay to agents</p>
            <p className="mt-1 text-lg font-semibold text-emerald-800 tabular-nums">
              {owesLoading && !owesFetched
                ? '…'
                : owesFetched
                  ? fullCurrency(payToAgents)
                  : '—'}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-500">
              {owesLoading && !owesFetched
                ? 'Loading…'
                : owesFetched
                  ? `${owesAgents.length} agent${owesAgents.length === 1 ? '' : 's'}`
                  : 'Click to view'}
            </p>
          </button>
      </div>

      <p className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span className="font-semibold text-blue-700">
          {tab === 'company_owes' || tab === 'company_paid'
            ? `${owesAgents.length} agent${owesAgents.length === 1 ? '' : 's'} to pay`
            : `${Number(counts[tab]) || 0} request${(Number(counts[tab]) || 0) === 1 ? '' : 's'} in ${TABS.find((item) => item.id === tab)?.label?.toLowerCase() || tab}`}
        </span>
        <span>
          Period total {(Number(counts.pending) || 0) + (Number(counts.approved) || 0) + (Number(counts.rejected) || 0)}
          {' · '}Pending {counts.pending || 0}
          {' · '}Approved {counts.approved || 0}
          {' · '}Rejected {counts.rejected || 0}
        </span>
      </p>

      {loading && tab !== 'company_owes' && tab !== 'company_paid' ? (
        <Loader fullScreen={false} message="Loading requests..." />
      ) : tab === 'company_owes' || tab === 'company_paid' ? (
        <>
        <div ref={owesSectionRef} className="mb-4 scroll-mt-4 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-800">Company owes</h2>
              <p className="mt-0.5 text-xs text-gray-500">Commission the company still has to pay. View shows every record. Settle pays them together. A payment image is optional.</p>
            </div>
            <p className="shrink-0 text-sm font-semibold tabular-nums text-emerald-800">
              {owesLoading && !owesFetched
                ? '…'
                : `${owesAgents.length} agent${owesAgents.length === 1 ? '' : 's'} · ${fullCurrency(payToAgents)}`}
            </p>
          </div>
          {owesLoading && !owesAgents.length ? (
            <p className="text-sm text-gray-400 text-center py-10">Loading company owes…</p>
          ) : !owesAgents.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No agent to pay in this period</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Hub / Branch</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Records</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {owesSlice.map((row) => (
                      <tr key={row.user_id}>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.name || row.user_id}</td>
                        <td className="px-4 py-2.5 text-gray-700">{row.hub_name || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-700">
                          {(row.sources || []).length} record{(row.sources || []).length === 1 ? '' : 's'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold text-emerald-800 tabular-nums">{fullCurrency(row.amount)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="inline-flex items-center justify-end gap-1">
                            <button
                              type="button"
                              title="View records"
                              aria-label="View records"
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
                              onClick={() => setOwesView(row)}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {canSettleOwes ? (
                              <button
                                type="button"
                                title="Settle"
                                aria-label="Settle"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-50"
                                onClick={() => {
                                  setPayAgentId(row.user_id);
                                  setPayOpen(true);
                                }}
                              >
                                <Wallet className="w-4 h-4" />
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPagination
                currentPage={owesPageSafe}
                totalPages={owesTotalPages}
                onPrevious={() => setOwesPage((page) => Math.max(1, page - 1))}
                onNext={() => setOwesPage((page) => Math.min(owesTotalPages, page + 1))}
                itemCount={owesSlice.length}
                itemLabel="agent"
              />
            </>
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">What we paid</h2>
            <p className="text-xs text-gray-500 mt-0.5">Payments already sent to agents in this period.</p>
          </div>
          {!companyPayments.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No company payment in this period</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Payment</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Mode</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companyPayments.map((row) => (
                    <tr key={row.expense_id}>
                      <td className="px-4 py-2.5 font-mono text-xs">{row.expense_id}</td>
                      <td className="px-4 py-2.5">{row.paid_to || row.paid_user_id || '—'}</td>
                      <td className="px-4 py-2.5">{formatIstDateTime(row.payment_date || row.expense_date) || '—'}</td>
                      <td className="px-4 py-2.5">{row.payment_mode || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-emerald-800 tabular-nums">{fullCurrency(row.amount)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="View records"
                            aria-label="View records"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
                            onClick={() => setPaidView(row)}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Download Excel"
                            aria-label="Download Excel"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-sky-800 hover:bg-sky-50"
                            onClick={() => downloadLinesExcel(
                              excelName(row.expense_id || row.paid_to),
                              row.lines,
                            )}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {!rows.length ? (
            <p className="text-sm text-gray-400 text-center py-10">No requests in this tab</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold">Batch</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Hub / Branch</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                    <th className="text-right px-4 py-2.5 font-semibold">To Pay</th>
                    <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                    <th className="text-left px-4 py-2.5 font-semibold">
                      {tab === 'rejected' ? 'Rejected by' : 'Approved by'}
                    </th>
                    <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => {
                    const saving = isLiveSavingRow(row);
                    const decided = row.status === 'APPROVED' || row.status === 'REJECTED' || saving;
                    const review = reviewFields(row);
                    return (
                    <Fragment key={row.batch_id}>
                      <tr
                        className={saving ? 'bg-sky-50/50' : 'hover:bg-sky-50/40 cursor-pointer'}
                        onClick={() => { if (!saving) openView(row); }}
                      >
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {saving ? (
                            <span className="text-sky-800">Saving…</span>
                          ) : (
                            <button type="button" className="underline" onClick={() => openView(row)}>
                              {row.batch_id}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <p className="font-medium text-gray-900 leading-tight">{row.agent_name}</p>
                          <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                            {formatIstDateTime(row.submitted_at)}
                          </p>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700">{row.hub_label || '—'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${saving ? 'bg-sky-50 text-sky-800 border-sky-200' : statusBadge(row.status)}`}>
                            {saving ? 'Saving' : statusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.to_pay_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fullCurrency(row.total_amount)}</td>
                        <td className="px-4 py-2.5">
                          {decided ? (
                            <>
                              <p className="text-gray-900 leading-tight">{settlementReviewerName(review)}</p>
                              <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                                {formatIstDateTime(review.reviewed_at)}
                              </p>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            className="relative menu-container inline-flex"
                            isOpen={openMenuId === row.batch_id}
                            onToggle={() => setOpenMenuId(openMenuId === row.batch_id ? null : row.batch_id)}
                            onClose={() => setOpenMenuId(null)}
                            onView={saving ? undefined : () => openView(row)}
                            onEdit={!saving && canEditSettlement(user?.role, row.status) && busyId !== row.batch_id
                              ? () => onEdit(row)
                              : undefined}
                            editLabel="Edit amount"
                            onDelete={!saving && canDeleteSettlement(user?.role, row.status) && busyId !== row.batch_id
                              ? () => onDelete(row.batch_id)
                              : undefined}
                            extraItems={canReview(row.status) && busyId !== row.batch_id ? [
                              {
                                key: 'approve',
                                icon: '✅',
                                label: 'Approve',
                                variant: 'success',
                                onClick: () => onApprove(row.batch_id),
                              },
                              {
                                key: 'reject',
                                icon: '❌',
                                label: 'Reject',
                                variant: 'danger',
                                onClick: () => onReject(row.batch_id),
                              },
                            ] : []}
                          />
                        </td>
                      </tr>
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {owesView ? (
        <SettlementModalShell
          title={owesView.name || owesView.user_id}
          subtitle={`${owesView.hub_name || 'Company owes'} · ${fullCurrency(owesView.amount)}`}
          size="md"
          onClose={() => setOwesView(null)}
          footer={(
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                icon={Download}
                onClick={() => downloadLinesExcel(excelName(owesView.name || owesView.user_id), owesView.sources)}
              >
                Download Excel
              </Button>
              <Button variant="secondary" onClick={() => setOwesView(null)}>Close</Button>
              {canSettleOwes ? (
                <Button
                  variant="primary"
                  icon={Wallet}
                  onClick={() => {
                    setPayAgentId(owesView.user_id);
                    setOwesView(null);
                    setPayOpen(true);
                  }}
                >
                  Settle
                </Button>
              ) : null}
            </div>
          )}
        >
          <div className="space-y-2">
            <p className="text-xs text-gray-500">These are the records this payment comes from.</p>
            <div className="rounded-xl border border-gray-100 overflow-hidden max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">ML Sheet</th>
                    <th className="text-left px-3 py-2 font-semibold">Source</th>
                    <th className="text-right px-3 py-2 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(owesView.sources || []).map((source) => (
                    <tr key={`${owesView.user_id}-${source.kind || ''}-${source.ml_sheet_id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{source.ml_sheet_id || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{source.location || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fullCurrency(source.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {paidView ? (
        <SettlementModalShell
          title={paidView.paid_to || paidView.paid_user_id || 'Payment'}
          subtitle={`${paidView.expense_id || ''} · ${fullCurrency(paidView.amount)}`}
          size="md"
          onClose={() => setPaidView(null)}
          footer={(
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                icon={Download}
                onClick={() => downloadLinesExcel(
                  excelName(paidView.expense_id || paidView.paid_to),
                  paidView.lines,
                )}
              >
                Download Excel
              </Button>
              <Button variant="secondary" onClick={() => setPaidView(null)}>Close</Button>
            </div>
          )}
        >
          <div className="space-y-2">
            <p className="text-xs text-gray-500">These are the records included in this payment.</p>
            <div className="rounded-xl border border-gray-100 overflow-hidden max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">ML Sheet</th>
                    <th className="text-left px-3 py-2 font-semibold">Source</th>
                    <th className="text-right px-3 py-2 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(paidView.lines || []).map((source, index) => (
                    <tr key={`${paidView.expense_id}-${source.ml_sheet_id || index}`}>
                      <td className="px-3 py-2 font-mono text-xs">{source.ml_sheet_id || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{source.location || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fullCurrency(source.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {payOpen ? (
        <SettlementModalShell
          title="Pay agent"
          subtitle="Every open amount in this period is paid together and approved. A payment image is optional."
          size="md"
          onClose={() => { if (!paying) { setPayOpen(false); setPayFile(null); } }}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" disabled={paying} onClick={() => { setPayOpen(false); setPayFile(null); }}>Cancel</Button>
              <Button variant="primary" icon={paying ? undefined : Wallet} disabled={paying} onClick={onPayAgent}>
                {paying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {paying ? 'Paying…' : 'Pay and settle'}
              </Button>
            </div>
          )}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Agent</label>
              <select
                value={payAgentId || paySelected?.user_id || ''}
                onChange={(e) => setPayAgentId(e.target.value)}
                className={formSelectClass()}
              >
                {owesAgents.map((row) => (
                  <option key={row.user_id} value={row.user_id}>
                    {row.name} · {fullCurrency(row.amount)}
                  </option>
                ))}
              </select>
            </div>
            {(paySelected?.sources || []).length ? (
              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 space-y-1 max-h-40 overflow-y-auto">
                <p className="text-xs font-medium text-gray-600">Records in this period</p>
                {paySelected.sources.map((source) => (
                  <p key={`${paySelected.user_id}-${source.ml_sheet_id}`} className="text-sm text-gray-800 flex justify-between gap-3">
                    <span>
                      <span className="font-mono text-xs">{source.ml_sheet_id || 'Record'}</span>
                      {source.location ? ` · ${source.location}` : ''}
                    </span>
                    <span className="tabular-nums font-medium">{fullCurrency(source.amount)}</span>
                  </p>
                ))}
              </div>
            ) : null}
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
              <p className="text-xs text-emerald-800">One payment for all of these</p>
              <p className="text-lg font-semibold text-emerald-900 tabular-nums">
                {fullCurrency(paySelected?.amount)}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment image (optional)</label>
              <MediaCaptureField mode="document" onPick={(file) => setPayFile(file || null)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
              <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className={formSelectClass()}>
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>{mode.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
              <input
                value={payRemarks}
                onChange={(e) => setPayRemarks(e.target.value)}
                className={formInputClass()}
                placeholder="Optional"
              />
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {editBatch ? (
        <SettlementModalShell
          title="Edit settlement amount"
          subtitle={editBatch.batch_id}
          size="md"
          onClose={() => { if (busyId !== editBatch.batch_id) setEditBatch(null); }}
          footer={(
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200"
                onClick={() => setEditBatch(null)}
                disabled={busyId === editBatch.batch_id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg bg-sky-600 text-white disabled:opacity-60"
                onClick={saveEdit}
                disabled={busyId === editBatch.batch_id}
              >
                Save amount
              </button>
            </div>
          )}
        >
          <div className="space-y-3 text-sm">
            <p className="text-gray-600">
              {editBatch.agent_name} · current {fullCurrency(editBatch.total_amount)}
            </p>
            <p className="text-xs text-gray-500">
              This is the money returned to the company. A higher amount fills Paid first, then To-Pay. A lower amount comes off To-Pay first, then Paid.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">New amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={editAmount}
                onChange={(e) => setEditAmount(e.target.value)}
                className={formInputClass()}
              />
            </div>
          </div>
        </SettlementModalShell>
      ) : null}

      {viewBatch ? (
        <CompanySettlementBatchView
          batch={viewBatch}
          onClose={() => setViewBatch(null)}
          canReview={canReview(viewBatch.status)}
          canDelete={canDeleteSettlement(user?.role, viewBatch.status)}
          busy={busyId === viewBatch.batch_id}
          onApprove={onApprove}
          onReject={onReject}
          onDelete={onDelete}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AccountantCompanySettlementApprovals;
